import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useState } from 'react'
import { TooltipProvider } from '@/components/ui/tooltip'

import { ThemeProvider } from '@/components/theme/ThemeProvider'
import {
  DeferredNotFound,
  DeferredRouteError,
} from '@/components/layout/deferredRouteChrome'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'

interface RouterContext {
  queryClient: QueryClient
}

// Router devtools only in dev, lazily (kept out of the prod bundle).
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: DeferredNotFound,
  errorComponent: DeferredRouteError,
})

function RootComponent() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={0}>
        {/* The app shell lives on the pathless _app layout (auth-guarded);
            /login renders standalone. */}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
        <DeferredToaster />
        <DeferredRouterDevtools />
      </TooltipProvider>
    </ThemeProvider>
  )
}

/** Sonner is unused until a toast fires. Mount it after the first paint. */
function DeferredToaster() {
  const [Toaster, setToaster] = useState<
    typeof import('@/components/ui/sonner').Toaster | null
  >(null)
  useEffect(() => {
    void import('@/components/ui/sonner').then((mod) => {
      setToaster(() => mod.Toaster)
    })
  }, [])
  return Toaster ? <Toaster /> : null
}

function DeferredRouterDevtools() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (import.meta.env.PROD) return
    const id = window.setTimeout(() => setShow(true), 4000)
    return () => window.clearTimeout(id)
  }, [])
  if (!show) return null
  return (
    <Suspense>
      <RouterDevtools position="bottom-right" />
    </Suspense>
  )
}
